// OrganisationsPage — the ranked board (BUILD-BRIEF §6, organisations agent).
//
// Mast → five-cell rail (HELD · MONITORED[unknown] · OPEN DEALS · SIGNALS[unknown] · CLIENTS)
// → instr-band (filter chips + search) → one zone of sheet rows. MONITORED and SIGNALS have no
// producer yet in round 1 (no monitors/signals table): they render `fv--unknown` "not
// collected", never a zero — a zero is a counted fact and this is the absence of counting.
import { useCallback, useEffect, useMemo, useState } from 'react';
import EmptyState from '../components/EmptyState.js';
import { OrgLogo } from '../components/Identity.js';
import ConfirmModal from '../components/ConfirmModal.js';
import { useToast } from '../components/Toast.js';
import { Mast, PageShell, Rail, RailCell, Sheet, Zone, ZoneHead } from '../components/PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open } from '../routes.js';
import {
  createOrganisation, listOrganisations,
  type OrganisationListItem, type OrganisationsListResponse,
} from '../api/organisations.js';
import { ApiError } from '../api/client.js';
// The signals rail cells and the "With a signal" chip read one summary (BUILD-BRIEF-R2 §8).
// It is a separate call on purpose: a failed poll must leave the cells at "not collected"
// rather than turning the whole board into an error.
import { signalsSummary, type SignalsSummary } from '../api/signals.js';
// "Find duplicates" runs the finder, which FILES PROPOSALS and writes nothing else (P1). The
// merge, the parent link and the dismissal all happen in Review, under an operator's click.
import { runDedupe } from '../api/dedupe.js';
import type { OrganisationStatus } from '../../shared/types.js';
import './OrganisationsPage.de.js';
import './OrganisationsPage.fr.js';

type ChipKey = 'all' | 'active' | 'dormant' | 'archived' | 'clients' | 'signal';

const SECTOR_FALLBACK = '—';

export default function OrganisationsPage() {
  const t = useT();
  const { fmtMoney, fmtNum } = useLocale();
  const toast = useToast();

  const [q, setQ] = useState('');
  const [chip, setChip] = useState<ChipKey>('all');
  const [data, setData] = useState<OrganisationsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [signals, setSignals] = useState<SignalsSummary | null>(null);

  const [finding, setFinding] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status: OrganisationStatus | '' = chip === 'active' || chip === 'dormant' || chip === 'archived' ? chip : '';
      const res = await listOrganisations({ q: q.trim() || undefined, status: status || undefined, client: chip === 'clients' });
      setData(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [q, chip]);

  useEffect(() => { void load(); }, [load]);

  // A failed poll is silence: `signals` stays null and both cells read "not collected", which
  // is a different fact from a counted zero.
  useEffect(() => {
    let live = true;
    void signalsSummary().then(
      (s) => { if (live) setSignals(s); },
      () => { if (live) setSignals(null); },
    );
    return () => { live = false; };
  }, []);

  const submitNew = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const org = await createOrganisation({ name });
      setNewOpen(false);
      setNewName('');
      toast('ok', t('{name} created', { name: org.name }));
      open(ROUTES.org({ id: org.id }));
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not create the organisation.'));
    } finally {
      setCreating(false);
    }
  }, [newName, toast, t]);

  /** The finder proposes; Review decides. The toast counts all three outcomes honestly: what it
   *  filed, what a dismissal already settled, and what is still waiting for a click. */
  const findDuplicates = useCallback(async () => {
    setFinding(true);
    try {
      const res = await runDedupe();
      // "already decided" is both settled shapes: a held dismissal and a held relate. Neither is
      // proposed again, and neither is a failure — so neither is counted as one.
      toast(res.proposed ? 'ok' : 'warn', t('{n} pairs proposed · {m} already decided · {k} pending in Review', {
        n: res.proposed, m: res.skipped_dismissed + res.skipped_related, k: res.skipped_pending,
      }));
      open(ROUTES.review({ kind: 'dedupe' }));
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('The duplicate finder could not run. Nothing was proposed.'));
    } finally {
      setFinding(false);
    }
  }, [toast, t]);

  const withSignal = useMemo(() => new Set(signals?.organisation_ids_open ?? []), [signals]);
  const allItems = data?.items ?? [];
  // A row a held merge archived is history: the survivor carries the record now, and showing
  // both names on the board would undo the visible outcome of the dedupe the operator just
  // held. It stays reachable under the Archived chip, where the whole point is to read what
  // was retired. The rail's HELD figure already excludes archived rows, so this is also what
  // keeps the count and the list saying the same thing.
  const boardItems = chip === 'archived' ? allItems : allItems.filter((o) => !o.merged_into);
  const items = chip === 'signal' ? boardItems.filter((o) => withSignal.has(o.id)) : boardItems;
  const summary = data?.summary;

  return (
    <PageShell>
      <Mast
        kicker={t('Network')}
        title={t('Organisations')}
        sub={
          summary
            ? <>
                <span className="voice-figure">{fmtNum(summary.held)}</span> {t('held')}
                <span className="sep">·</span>
                <span className="voice-figure">{fmtNum(summary.open_deals)}</span> {t('with an open deal')}
                <span className="sep">·</span>
                <span className="voice-figure">{fmtNum(summary.clients)}</span> {t('clients')}
              </>
            : t('loading…')
        }
        verbs={<>
          <button
            type="button"
            className="btn"
            disabled={finding}
            title={t('Look for organisation rows that are the same organisation. It proposes; nothing merges without a click in Review.')}
            onClick={() => { void findDuplicates(); }}
          >
            {finding ? t('Looking…') : t('Find duplicates')}
          </button>
          <button type="button" className="btn btn-accent" onClick={() => setNewOpen(true)}>{t('New organisation')}</button>
        </>}
      />

      <Rail after={
        <div className="instr-band">
          <div className="filter-chips" style={{ margin: 0 }}>
            {(
              [
                ['all', 'All organisations'], ['active', 'Active'], ['dormant', 'Dormant'],
                ['archived', 'Archived'], ['clients', 'Clients'], ['signal', 'With a signal'],
              ] as Array<[ChipKey, string]>
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`chip${chip === key ? ' active' : ''}`}
                aria-pressed={chip === key}
                onClick={() => setChip(key)}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <div className="search-wrapper ml-auto" style={{ flex: '0 1 280px' }}>
            <input
              className="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('Name, UID, domain…')}
            />
          </div>
        </div>
      }>
        <RailCell
          label={t('Held')}
          value={summary ? fmtNum(summary.held) : '—'}
          detail={t('organisations on the record, not archived')}
        />
        <RailCell
          label={t('Monitored')}
          value={signals ? fmtNum(signals.monitors_organisations) : t('not collected')}
          tone={!signals ? 'unknown' : signals.monitors_organisations === 0 ? 'nil' : 'ink'}
          detail={signals
            ? `${fmtNum(signals.monitors_total)} ${t('monitors in all')} · ${fmtNum(signals.runs_24h)} ${t('runs in 24h')}`
            : t('the monitors could not be read')}
        />
        <RailCell
          label={t('Open deals')}
          value={summary ? fmtNum(summary.open_deals) : '—'}
          detail={summary ? `${fmtMoney(summary.open_deals_value)} ${t('face value')}` : ' '}
        />
        <RailCell
          label={t('Signals · 7d')}
          go={chip === 'signal' ? undefined : '→'}
          value={signals ? fmtNum(signals.last_7d) : t('not collected')}
          tone={!signals ? 'unknown' : signals.last_7d === 0 ? 'nil' : signals.new_by_severity.high > 0 ? 'broken' : 'attend'}
          detail={signals
            ? `${fmtNum(signals.organisation_ids_7d.length)} ${t('organisations')} · ${fmtNum(signals.new)} ${t('waiting for a decision')}`
            : t('no monitor has run yet')}
          onClick={() => setChip(chip === 'signal' ? 'all' : 'signal')}
          pressed={chip === 'signal'}
        />
        <RailCell
          label={t('Clients')}
          value={summary ? fmtNum(summary.clients) : '—'}
          detail={t('linked to Signet engagements')}
        />
      </Rail>

      <Zone>
        {/* The head counts the rows that are actually standing here: the server's total counts
            the query, and this list drops the merged-away and the signal-less. */}
        <ZoneHead label={t('Organisations')} count={data ? fmtNum(items.length) : undefined} />
        {error ? (
          <EmptyState kind="error" title={t('Could not load')} detail={error} onRetry={load} />
        ) : !loading && items.length === 0 ? (
          <EmptyState
            kind={q || chip !== 'all' ? 'no-match' : 'empty'}
            title={q || chip !== 'all' ? t('Nothing matches') : t('Nothing here yet')}
            detail={
              q || chip !== 'all'
                ? t('No organisation matches this filter.')
                : t('No organisation has been recorded yet. Add one, or run the seed import.')
            }
            onClear={q || chip !== 'all' ? () => { setQ(''); setChip('all'); } : undefined}
          />
        ) : (
          <Sheet clip>
            {items.map((o) => <OrgRow key={o.id} o={o} />)}
          </Sheet>
        )}
      </Zone>

      <ConfirmModal
        open={newOpen}
        title={t('New organisation')}
        confirmLabel={creating ? t('Saving…') : t('Save')}
        onConfirm={submitNew}
        onCancel={() => { setNewOpen(false); setNewName(''); }}
      >
        <label className="u-label" htmlFor="org-new-name">{t('Name')}</label>
        <input
          id="org-new-name"
          className="search-input"
          style={{ width: '100%' }}
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void submitNew(); }}
        />
      </ConfirmModal>
    </PageShell>
  );
}

function OrgRow({ o }: { o: OrganisationListItem }) {
  const t = useT();
  const { fmtMoney, fmtNum, tp } = useLocale();
  const meta = [o.legal_form, o.seat, o.canton].filter(Boolean).join(' · ') || SECTOR_FALLBACK;
  return (
    <button
      type="button"
      className="row row--btn"
      style={{ gridTemplateColumns: '1fr 140px 130px 100px' }}
      onClick={() => open(ROUTES.org({ id: o.id }))}
    >
      {/* The organisation's own logo stands on the board, not only inside the dossier: the mark
          is how a row is recognised before its name is read. `OrgLogo` asks the presence
          endpoint ONCE for the whole page and renders initials for every organisation whose
          logo nobody has fetched — an absent picture costs no request and no console line. */}
      <span className="idcell">
        <OrgLogo id={o.id} name={o.name} />
        <span>
          <span className="w">{o.name}</span>
          <span className="d"> · {meta} · <span className="src-ref">{o.ref}</span></span>
          {(o.sector || (o.domains?.length ?? 0) > 0) && (
            <>
              <br />
              <span className="d">
                {[o.sector, o.domains?.[0]].filter(Boolean).join(' · ')}
              </span>
            </>
          )}
        </span>
      </span>
      {/* A fixed '{n} people' read '1 people' on most of the board; the count picks the form.
          The units figure (§2b) stands beside it only when there ARE units: a row the server
          did not count carries `units_count` undefined, and an organisation with none carries
          zero — neither is written as "0 units". */}
      <span
        className="d"
        title={typeof o.people_in_tree === 'number' && o.people_in_tree !== o.people_count
          ? t('{n} in this tree, units included', { n: fmtNum(o.people_in_tree) })
          : undefined}
      >
        {tp(o.people_count, '{n} person', '{n} people', { n: fmtNum(o.people_count) })}
        {typeof o.units_count === 'number' && o.units_count > 0 && (
          <> · {tp(o.units_count, '{n} unit', '{n} units', { n: fmtNum(o.units_count) })}</>
        )}
      </span>
      <span className="d">
        {o.open_opportunities_count
          ? `${fmtNum(o.open_opportunities_count)} ${t('deals')} · ${fmtMoney(o.open_face_value)}`
          : t('no open deal')}
      </span>
      <span>
        {o.status !== 'active' && <span className={`tag${o.status === 'archived' ? ' tag--ghost' : ''}`}>{t(o.status)}</span>}
        {o.is_client && <span className="tag tag--ok">{t('client')}</span>}
      </span>
    </button>
  );
}
